import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { getQueryParamsObjectToString } from 'utils/helpers/router'
import {
  fetchTnCList,
  putEditTnC,
  deleteTnC,
  postAddTnC,
  mapPaginatedListToLegacyQuery,
} from 'features/Enterprise/SKP/utils/apiList'

import { sliceName, setLoading, setParams, setTnCList, setPageNumber } from './tncSlice'

const parseTnCMutationResponse = (data: {
  message?: string
  error?: { status?: boolean; message?: string }
}) => {
  if (data.error?.status) {
    throw new Error(data.error.message || data.message || 'Request failed')
  }

  return data.message || 'Success'
}

export const getTnCList = createAsyncThunk(
  `${sliceName}/getTnCList`,
  async (_, { getState, dispatch }) => {
    try {
      dispatch(setLoading(true))
      const {
        tnc: { params },
      } = getState() as StoreStateType

      const pageSize = params.limit || 5
      const pageIndex = pageSize ? Math.floor(params.offset / pageSize) + 1 : 1
      const parseQuery = getQueryParamsObjectToString({
        page_size: pageSize,
        page_index: pageIndex,
        promotion_type: params.promotion_type,
        tnc_title: params.tnc_title,
        tnc_description: params.tnc_description,
      })
      const { data } = await fetchTnCList(parseQuery)
      const normalized = mapPaginatedListToLegacyQuery(data, pageSize, params.offset)
      dispatch(setTnCList(normalized.data))
      dispatch(
        setPageNumber({
          offset: normalized.query.offset,
          totalItem: normalized.query.total,
          limit: normalized.query.limit,
        }),
      )
      dispatch(setLoading(false))
      return normalized.data
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const editTnC = createAsyncThunk(
  `${sliceName}/editTnC`,
  async (
    payload: {
      promotion_type: string
      tnc_description: string
      tnc_status: string
      tnc_title: string
      id: number
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))

      const { data } = await putEditTnC(
        {
          promotion_type: payload.promotion_type,
          tnc_description: payload.tnc_description,
          tnc_status: payload.tnc_status,
          tnc_title: payload.tnc_title,
        },
        payload.id,
      )
      await dispatch(getTnCList())
      dispatch(setLoading(false))
      return parseTnCMutationResponse(data)
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const deleteTnc = createAsyncThunk(
  `${sliceName}/deleteTnc`,
  async (
    payload: {
      id: number
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))

      await deleteTnC(payload.id)
      await dispatch(getTnCList())
      return true
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const addTnC = createAsyncThunk(
  `${sliceName}/addTnC`,
  async (
    payload: {
      promotion_type: string
      tnc_description: string
      tnc_status: string
      tnc_title: string
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      const { data } = await postAddTnC({
        promotion_type: payload.promotion_type,
        tnc_description: payload.tnc_description,
        tnc_status: payload.tnc_status,
        tnc_title: payload.tnc_title,
      })
      const message = parseTnCMutationResponse(data)
      const {
        tnc: { params },
      } = getState() as StoreStateType
      dispatch(setParams({ ...params, offset: 0 }))
      await dispatch(getTnCList())
      dispatch(setLoading(false))
      return message
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)
